import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';

import type { JSONValue } from './apiClient';

// lodash's converters treat a leading `_` as a word separator and drop it
// (`_destroy` -> `destroy`), which breaks Rails nested-attributes deletion
// markers and any other leading-underscore convention. Round-trip it by
// transforming only the part of the key after the leading run.
function withLeadingUnderscoresPreserved(
  key: string,
  transformKey: (input: string) => string
): string {
  const leadingUnderscores = /^_+/.exec(key)?.[0] ?? '';
  return (
    leadingUnderscores + transformKey(key.slice(leadingUnderscores.length))
  );
}

// `Object.entries`/`fromEntries` walk own enumerable properties, which is
// correct for plain `{}` payload objects but silently flattens instances
// like `Date`/`File`/`Blob` (no own enumerable properties) into `{}`. Only
// walk plain objects; pass anything else through untouched.
function isPlainObject(value: object): boolean {
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function transformKeys(
  json: JSONValue,
  transformKey: (key: string) => string
): JSONValue {
  if (Array.isArray(json)) {
    return json.map(item => transformKeys(item, transformKey));
  }
  if (json !== null && typeof json === 'object') {
    if (!isPlainObject(json)) {
      return json;
    }
    const converted: Record<string, JSONValue> = {};
    for (const [key, value] of Object.entries(json)) {
      const convertedKey = withLeadingUnderscoresPreserved(key, transformKey);
      // Two distinct keys converting to the same key would silently drop
      // one of them (`Object.fromEntries` keeps only the last). Fail loudly
      // instead — this transform has no correct answer for a collision.
      if (Object.prototype.hasOwnProperty.call(converted, convertedKey)) {
        throw new Error(
          `casing conversion collision: "${key}" and another key both convert to "${convertedKey}"`
        );
      }
      converted[convertedKey] = transformKeys(value, transformKey);
    }
    return converted;
  }
  return json;
}

// Request payloads are written in camelCase and converted to the API's
// snake_case at the `apiClient` boundary. Digit-adjacent and acronym keys
// convert deterministically but not always losslessly for the wire (e.g.
// `utf8` -> `utf_8`, `HTMLParser` -> `html_parser`) — this is lodash's
// standard word-boundary splitting, not a bug; avoid `casing:'snake'` for
// endpoints whose params depend on a specific one of those literal keys.
//
// The type parameter is the caller's assertion about the converted shape,
// same as `camelCaseKeys` below and `apiClient<T>` — there is no runtime
// validation.
export function snakeCaseKeys<T extends JSONValue = JSONValue>(
  json: JSONValue
): T {
  return transformKeys(json, snakeCase) as T;
}

// The type parameter is the caller's assertion about the converted shape,
// same as `apiClient<T>` — there is no runtime validation.
export function camelCaseKeys<T extends JSONValue = JSONValue>(
  json: JSONValue
): T {
  return transformKeys(json, camelCase) as T;
}
